import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FiSave, FiDollarSign, FiSmartphone, FiImage, FiGrid, FiLoader } from 'react-icons/fi';
import { FaApple } from 'react-icons/fa';

const PricingManager = () => {
  const [pricing, setPricing] = useState({
    base: 119.90,
    logo: 30.00,
    loader: 10.00,
    platforms: 0,
    menu: 0,
    icons: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'settings', 'pricing');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPricing({
          base: data.base ?? 119.90,
          logo: data.logo ?? 30.00,
          loader: data.loader ?? 10.00,
          platforms: data.platforms ?? 0,
          menu: data.menu ?? 0,
          icons: data.icons ?? 0,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar preços:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await setDoc(doc(db, 'settings', 'pricing'), pricing, { merge: true });
      setMessage('Valores salvos com sucesso!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error("Erro ao salvar:", error);
      setMessage('Erro ao salvar os valores.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setPricing({
      base: 119.90,
      logo: 30.00,
      loader: 10.00,
      platforms: 0,
      menu: 0,
      icons: 0,
    });
    setMessage('Valores restaurados para o padrão. Não esqueça de clicar em Salvar!');
    setTimeout(() => setMessage(''), 4000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPricing((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Carregando valores...</div>;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 md:p-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FiDollarSign className="text-emerald-500" /> Tabela de Preços (SaaS)
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Defina os valores cobrados em cada etapa da criação do aplicativo.
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={handleResetDefaults}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-bold rounded-xl transition-all shadow-sm disabled:opacity-70 whitespace-nowrap"
          >
            Resetar Padrão
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all shadow-sm disabled:opacity-70 whitespace-nowrap"
          >
            {saving ? 'Salvando...' : <><FiSave /> Salvar Valores</>}
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-xl font-medium flex items-center gap-2 ${message.includes('Erro') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* BASE */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <FiSmartphone size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Plano Base (Step 1)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Valor fixo cobrado inicialmente</p>
            </div>
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R$</span>
            <input
              type="number"
              step="0.01"
              name="base"
              value={pricing.base}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white font-bold text-lg"
            />
          </div>
        </div>

        {/* LOGO */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
              <FiImage size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Criar Logo (Step 2)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Adicional se o cliente não tiver logo</p>
            </div>
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R$</span>
            <input
              type="number"
              step="0.01"
              name="logo"
              value={pricing.logo}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white font-bold text-lg"
            />
          </div>
        </div>

        {/* LOADER */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400">
              <FiLoader size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Loader Customizado (Step 3)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Adicional para barra de carregamento VIP</p>
            </div>
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R$</span>
            <input
              type="number"
              step="0.01"
              name="loader"
              value={pricing.loader}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white font-bold text-lg"
            />
          </div>
        </div>

        {/* PLATAFORMAS */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-800 dark:text-slate-300">
              <FaApple size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Plataformas Extra (Step 4)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Ex: Adicional cobrado pelo iOS</p>
            </div>
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R$</span>
            <input
              type="number"
              step="0.01"
              name="platforms"
              value={pricing.platforms}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white font-bold text-lg"
            />
          </div>
        </div>

        {/* MENU */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <FiGrid size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Menu App (Step 5)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Adicional por habilitar Menu Inferior</p>
            </div>
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R$</span>
            <input
              type="number"
              step="0.01"
              name="menu"
              value={pricing.menu}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white font-bold text-lg"
            />
          </div>
        </div>

        {/* ICONS */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
              <FiGrid size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Valor por Ícone Extra (Step 5)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Custo de cada ícone adicionado no menu</p>
            </div>
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">R$</span>
            <input
              type="number"
              step="0.01"
              name="icons"
              value={pricing.icons}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white font-bold text-lg"
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default PricingManager;
